import { useState } from 'react';
import SEO from '@/components/SEO';
import { Download, FileText, ArrowRight, BookOpen } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ContactPopup from '@/components/ContactPopup';
import { Button } from '@/components/ui/button';

const Publications = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const publications = [
    {
      id: 1,
      title: 'The Complete Trademark Registration Guide 2025',
      description: 'A comprehensive 50-page guide covering everything from initial trademark searches to successful USPTO registration. Includes sample applications, timelines, and expert tips.',
      type: 'Guide',
      pages: '50 pages',
      format: 'PDF',
      size: '2.5 MB',
      image: 'https://images.unsplash.com/photo-1516534775068-ba3e7458af70',
      featured: true
    },
    {
      id: 2,
      title: 'Intellectual Property Protection for Startups',
      description: 'Essential strategies for early-stage companies to protect their innovations, brands, and creative works. Written specifically for entrepreneurs and startup founders.',
      type: 'Whitepaper',
      pages: '25 pages',
      format: 'PDF',
      size: '1.8 MB',
      image: 'https://images.unsplash.com/photo-1559136555-9303baea8ebd',
      featured: true
    },
    {
      id: 3,
      title: 'Copyright Law Essentials for Digital Creators',
      description: 'Navigate the complexities of copyright in the digital age. This guide covers online content protection, fair use, licensing, and enforcement strategies.',
      type: 'Guide',
      pages: '35 pages',
      format: 'PDF',
      size: '2.1 MB',
      image: 'https://images.unsplash.com/photo-1499951360447-b19be8fe80f5',
      featured: false
    },
    {
      id: 4,
      title: 'LLC Formation Checklist & Templates',
      description: 'Step-by-step checklist for forming an LLC, including operating agreement templates, filing requirements, and state-by-state guidelines.',
      type: 'Resource Kit',
      pages: '40 pages',
      format: 'PDF',
      size: '3.2 MB',
      image: 'https://images.unsplash.com/photo-1659869764315-dc3d188141fe',
      featured: false
    },
    {
      id: 5,
      title: 'Patent Application Best Practices',
      description: 'Learn the fundamentals of patent law and discover proven strategies for preparing strong patent applications that maximize protection.',
      type: 'Whitepaper',
      pages: '30 pages',
      format: 'PDF',
      size: '2.0 MB',
      image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef',
      featured: false
    },
    {
      id: 6,
      title: 'Brand Protection Strategy Workbook',
      description: 'Interactive workbook to help you develop a comprehensive brand protection strategy, including trademark portfolio management and enforcement planning.',
      type: 'Workbook',
      pages: '45 pages',
      format: 'PDF',
      size: '3.5 MB',
      image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3',
      featured: false
    }
  ];

  const featuredPublications = publications.filter(pub => pub.featured);
  const regularPublications = publications.filter(pub => !pub.featured);

  const getTypeColor = (type) => {
    const colors = {
      'Guide': 'bg-primary-100 hover:text-primary-600',
      'Whitepaper': 'bg-secondary-100 text-orange-700',
      'Resource Kit': 'bg-secondary-100 text-secondary-600',
      'Workbook': 'bg-blue-100 text-blue-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  return (
    <>
      <SEO 
        title="Free Publications & Resources - Trademark & IP Guides"
        description="Download free comprehensive guides, whitepapers, and resources on trademark registration, copyright law, patent protection, and business formation."
        keywords="trademark guide, IP resources, free legal guides, copyright handbook, business formation guide"
      />
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-12 md:py-16 overflow-hidden bg-primary-600">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(124,58,237,0.1),transparent_50%),radial-gradient(circle_at_70%_80%,rgba(139,92,246,0.1),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <div className="inline-block mb-4">
              <span className="bg-primary-600 text-white px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide">
                Free Resources
              </span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              <span className="block">Publications</span>
              <span className="block mt-2 bg-primary-600 text-white">
                & Resources
              </span>
            </h1>
            
            <p className="text-lg text-gray-300 leading-relaxed">
              Download our comprehensive guides, whitepapers, and resources to help you protect and grow your business.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Publications */}
      <section className="py-20 bg-transparent">
        <div className="container mx-auto px-4">
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <BookOpen className="w-6 h-6 text-primary-600" />
              <h2 className="text-3xl font-bold text-gray-900">Featured Publications</h2>
            </div>
            <p className="text-lg text-gray-600">Our most popular and comprehensive resources</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {featuredPublications.map((pub) => (
              <div
                key={pub.id}
                className="bg-white rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 border-2 border-primary-600"
              >
                <div className="relative h-64">
                  <img
                    src={pub.image}
                    alt={pub.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <span className={`px-3 py-1.5 rounded-full text-xs font-semibold ${getTypeColor(pub.type)}`}>
                      {pub.type}
                    </span>
                  </div>
                  <div className="absolute top-4 right-4">
                    <span className="bg-primary-600 text-white px-3 py-1.5 rounded-full text-xs font-semibold">
                      ⭐ Featured
                    </span>
                  </div>
                </div>
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {pub.title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {pub.description}
                  </p>
                  <div className="flex flex-wrap gap-3 mb-6 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <FileText className="w-4 h-4" />
                      <span>{pub.pages}</span>
                    </div>
                    <span>•</span>
                    <span>{pub.format}</span>
                    <span>•</span>
                    <span>{pub.size}</span>
                  </div>
                  <Button
                    onClick={() => setIsPopupOpen(true)}
                    className="w-full bg-primary-600 text-white py-3 rounded-full font-semibold hover:shadow-lg"
                  >
                    <Download className="w-5 h-5 mr-2" />
                    Download Free
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* All Publications */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">All Resources</h2>
            <p className="text-lg text-gray-600">Browse our complete library of publications</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {regularPublications.map((pub) => (
              <div
                key={pub.id}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 group"
              >
                <div className="relative h-48">
                  <img
                    src={pub.image}
                    alt={pub.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-4 left-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getTypeColor(pub.type)}`}>
                      {pub.type}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">
                    {pub.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {pub.description}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
                    <FileText className="w-3 h-3" />
                    <span>{pub.pages}</span>
                    <span>•</span>
                    <span>{pub.format}</span>
                    <span>•</span>
                    <span>{pub.size}</span>
                  </div>
                  <Button
                    onClick={() => setIsPopupOpen(true)}
                    className="w-full bg-primary-600 text-white py-2.5 rounded-full font-semibold hover:shadow-lg text-sm"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Need Personalized Legal Guidance?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Our expert team is here to provide tailored advice for your specific needs.
          </p>
         <div className="flex flex-wrap justify-center gap-4">
            <Button
             onClick={() => setIsPopupOpen(true)}
              className="bg-secondary-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:shadow-lg hover:border-white"
              data-testid="about-cta-btn"
            >
              Get Free Consultation
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <ContactPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </>
  );
};

export default Publications;